import { GlButton, GlFormGroup, GlAlert, GlTooltipDirective } from '@gitlab/ui';
import { s__ } from '~/locale';
import * as Sentry from '~/sentry/sentry_browser_wrapper';
import { helpPagePath } from '~/helpers/help_page_helper';
import { getIdFromGraphQLId } from '~/graphql_shared/utils';
import { trackSavedUsingEditor } from '~/vue_shared/components/markdown/tracking';
import { confirmAction } from '~/lib/utils/confirm_via_gl_modal/confirm_via_gl_modal';
import MarkdownEditor from '~/vue_shared/components/markdown/markdown_editor.vue';
import markdownEditorEventHub from '~/vue_shared/components/markdown/eventhub';
import { CLEAR_AUTOSAVE_ENTRY_EVENT } from '~/vue_shared/constants';
import { renderGFM } from '~/behaviors/markdown/render_gfm';
import { toggleCheckbox } from '~/behaviors/markdown/utils';
import SafeHtml from '~/vue_shared/directives/safe_html';
import updateDesignDescriptionMutation from '../graphql/update_design_description.mutation.graphql';
import { UPDATE_DESCRIPTION_ERROR } from '../constants';
const isCheckbox = (target)=>target?.classList.contains('task-list-item-checkbox');
export default {
    name: 'DesignDescription',
    components: {
        MarkdownEditor,
        GlAlert,
        GlButton,
        GlFormGroup
    },
    directives: {
        SafeHtml,
        GlTooltip: GlTooltipDirective
    },
    i18n: {
        editDescription: s__('DesignManagement|Edit description'),
        descriptionLabel: s__('DesignManagement|Description'),
        addDescriptionLabel: s__('DesignManagement|Add a description'),
        cancelEdit: s__('DesignManagement|Are you sure you want to cancel editing the description?'),
        primaryModalButton: s__('DesignManagement|Discard changes'),
        cancelModalButton: s__('DesignManagement|Continue editing')
    },
    formFieldProps: {
        id: 'design-description',
        name: 'design-description',
        placeholder: s__('DesignManagement|Write a comment or drag your files here…'),
        'aria-label': s__('DesignManagement|Design description')
    },
    markdownDocsPath: helpPagePath('user/markdown'),
    quickActionsDocsPath: helpPagePath('user/project/quick_actions'),
    props: {
        design: {
            type: Object,
            required: true
        },
        markdownPreviewPath: {
            type: String,
            required: true
        }
    },
    data () {
        return {
            descriptionText: this.design.description || '',
            showEditor: false,
            isSubmitting: false,
            errorMessage: '',
            isCancellingEdit: false,
            // eslint-disable-next-line @gitlab/no-hardcoded-urls -- autosave key identifier using entity type names, not a navigational URL
            autosaveKey: `WorkItem/${getIdFromGraphQLId(this.design.issue.id)}/Design/${getIdFromGraphQLId(this.design.id)}`
        };
    },
    computed: {
        canUpdate () {
            return this.design.issue?.userPermissions?.updateDesign && !this.showEditor;
        },
        showAddDescriptionButton () {
            return this.design.issue?.userPermissions?.updateDesign && !this.design.descriptionHtml;
        }
    },
    watch: {
        'design.descriptionHtml': {
            handler (newDescriptionHtml, oldDescriptionHtml) {
                if (newDescriptionHtml !== oldDescriptionHtml) {
                    this.renderGFM();
                }
            },
            immediate: true
        }
    },
    methods: {
        startEditing () {
            this.showEditor = true;
        },
        closeForm () {
            // If description text is empty on cancel,
            // restore the old description
            if (!this.descriptionText) {
                this.descriptionText = this.design.description;
            }
            this.showEditor = false;
        },
        async cancelDescription () {
            if (this.isCancellingEdit) return;
            if (this.descriptionText && this.descriptionText !== this.design.description) {
                this.isCancellingEdit = true;
                try {
                    const confirmed = await confirmAction(this.$options.i18n.cancelEdit, {
                        primaryBtnText: this.$options.i18n.primaryModalButton,
                        cancelBtnText: this.$options.i18n.cancelModalButton,
                        primaryBtnVariant: 'danger'
                    });
                    if (!confirmed) {
                        return;
                    }
                    // Update markdown's placeholder text based on if
                    // an existing description was being edited or
                    // a totally new description was being added
                    if (!this.design.descriptionHtml) {
                        this.descriptionText = '';
                    } else {
                        this.descriptionText = this.design.description;
                    }
                    markdownEditorEventHub.$emit(CLEAR_AUTOSAVE_ENTRY_EVENT, this.autosaveKey);
                    this.showEditor = false;
                    this.enableCheckboxes();
                } finally{
                    this.isCancellingEdit = false;
                }
            } else {
                this.descriptionText = this.design.description;
                this.showEditor = false;
                await this.$nextTick();
                this.enableCheckboxes();
            }
        },
        enableCheckboxes () {
            if (this.canUpdate) {
                const checkboxes = this.$el.querySelectorAll('.task-list-item-checkbox:not([data-inapplicable])');
                // enable boxes, disabled by default in markdown
                checkboxes.forEach((checkbox)=>{
                    // eslint-disable-next-line no-param-reassign
                    checkbox.disabled = false;
                });
            }
        },
        async renderGFM () {
            await this.$nextTick();
            renderGFM(this.$refs['gfm-content']);
            this.enableCheckboxes();
        },
        setDescriptionText (newText) {
            // Do not update when cmd+enter is executed
            if (!this.isSubmitting) {
                this.descriptionText = newText;
            }
        },
        async updateDesignDescription () {
            this.isSubmitting = true;
            if (this.$refs.markdownEditor) {
                trackSavedUsingEditor(this.$refs.markdownEditor.isContentEditorActive, 'WorkItem_DesignDescription');
            }
            try {
                const descriptionInput = {
                    description: this.descriptionText,
                    id: this.design.id
                };
                await this.$apollo.mutate({
                    mutation: updateDesignDescriptionMutation,
                    variables: {
                        input: descriptionInput
                    }
                });
                this.closeForm();
            } catch (error) {
                Sentry.captureException(error);
                this.errorMessage = UPDATE_DESCRIPTION_ERROR;
            } finally{
                await this.$nextTick();
                this.enableCheckboxes();
                this.isSubmitting = false;
            }
        },
        toggleCheckboxes (event) {
            const { target } = event;
            if (isCheckbox(target)) {
                target.disabled = true;
                // Toggle checkboxes based on user input
                const replacement = toggleCheckbox({
                    rawMarkdown: this.descriptionText,
                    checkboxChecked: target.checked,
                    target
                });
                if (!replacement) return;
                this.descriptionText = replacement.newMarkdown;
                // Update the description text using mutation
                this.updateDesignDescription();
            }
        }
    }
};
